import { useCallback, useEffect, useRef } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TYPES } from "@utils/inversifyJS/types";
import type { GetMyPermissionsService } from "@application/useCases/workspace/GetMyPermissionsService";
import type { ListProjectsService } from "@application/useCases/workspace/ListProjectsService";
import type { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { usePermissionStore } from "@presentation/stores/PermissionStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import type { PermissionScope } from "@domain/schemas/workspace";

const hasScope = (scope: PermissionScope): boolean => Boolean(scope.teamId) || Boolean(scope.projectId);

/**
 * Whether the caller has established who, if anyone, is signed in.
 *
 * `"unknown"` is not a convenience: session restoration is asynchronous, so a
 * plain boolean reads as "not authenticated" on the first render. Clearing the
 * workspace on that initial `false` wipes the persisted `activeTeamContext`
 * before restoration has had a chance to run, so a valid saved team and project
 * never survive a reload and there is nothing left for reconciliation to
 * recover. The workspace must only be cleared once authentication has
 * definitively failed.
 */
export type WorkspaceAuthStatus = "unknown" | "authenticated" | "unauthenticated";

/**
 * Hydrates the workspace once the user is authenticated, then keeps permissions
 * in step with the active context.
 *
 * Ordering matters and is not an implementation detail: teams and projects must
 * land before the context is reconciled, because reconciliation is what discards
 * ids that no longer resolve, and permissions must be requested only for a
 * reconciled scope - asking about a stale id wastes a round trip and caches an
 * answer under a scope key nothing will read.
 *
 * Teams and projects are fetched concurrently: `GET /projects` does not depend on
 * `GET /teams`, and a user can hold a project role in a team they are not a
 * member of, so neither list is derivable from the other.
 */
export const useWorkspaceHydration = (authStatus: WorkspaceAuthStatus, identityKey: string | null): { reload: () => Promise<void> } => {
  const listTeamsService = useInjection<ListTeamsService>(TYPES.ListTeamsService);
  const listProjectsService = useInjection<ListProjectsService>(TYPES.ListProjectsService);
  const getMyPermissionsService = useInjection<GetMyPermissionsService>(TYPES.GetMyPermissionsService);

  /**
   * Identifies the session a request was issued for.
   *
   * Every workspace request outlives the render that started it, so a logout or
   * an account switch can land between issuing one and its response arriving.
   * Without this, the resolved response would write the previous user's teams,
   * projects or permissions into stores that were just cleared, and the next
   * session would briefly render the previous user's workspace.
   *
   * Bumped in the auth effect's cleanup rather than its body, so teardown
   * invalidates in-flight work before the new run clears the stores, and so the
   * guard does not depend on effect declaration order or on the stability of
   * the injected service identities.
   */
  const authGenerationReference = useRef(0);
  const previousIdentityReference = useRef(identityKey);
  const hydratedIdentityReference = useRef<string | null>(null);

  const setWorkspace = useWorkspaceContextStore((state) => state.setWorkspace);
  const setStatus = useWorkspaceContextStore((state) => state.setStatus);
  const setError = useWorkspaceContextStore((state) => state.setError);
  const clearWorkspace = useWorkspaceContextStore((state) => state.clear);

  const activeTeamId = useWorkspaceContextStore((state) => state.activeTeamId);
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const workspaceStatus = useWorkspaceContextStore((state) => state.status);

  const setActiveScope = usePermissionStore((state) => state.setActiveScope);
  const markLoading = usePermissionStore((state) => state.markLoading);
  const setPermissions = usePermissionStore((state) => state.setPermissions);
  const setPermissionError = usePermissionStore((state) => state.setError);
  const clearPermissions = usePermissionStore((state) => state.clear);
  const permissionsRefetchNonce = usePermissionStore((state) => state.refetchNonce);

  const loadWorkspace = useCallback(async () => {
    const generation = authGenerationReference.current;
    setStatus("loading");
    try {
      const [teams, projects] = await Promise.all([listTeamsService.getMyTeamsAsync(), listProjectsService.getMyProjectsAsync()]);
      if (authGenerationReference.current !== generation) return;
      hydratedIdentityReference.current = identityKey;
      setWorkspace(teams, projects);
    } catch (error) {
      if (authGenerationReference.current !== generation) return;
      setError(error);
      setStatus("error");
    }
  }, [identityKey, listProjectsService, listTeamsService, setError, setStatus, setWorkspace]);

  useEffect(() => {
    // Session restoration has not finished. Doing anything here - including
    // clearing - would act on an identity that is not yet known.
    if (authStatus === "unknown") {
      return;
    }

    const isIdentityChanged = previousIdentityReference.current !== null && identityKey !== previousIdentityReference.current;
    previousIdentityReference.current = identityKey;

    if (authStatus === "unauthenticated") {
      hydratedIdentityReference.current = null;
      clearWorkspace();
      clearPermissions();
      return;
    }

    if (isIdentityChanged) {
      clearWorkspace();
      clearPermissions();
    }

    void loadWorkspace();

    // Runs before the next effect body, so anything still in flight is
    // invalidated before the stores are cleared or reloaded for a new session.
    return () => {
      authGenerationReference.current += 1;
    };
  }, [authStatus, clearPermissions, clearWorkspace, identityKey, loadWorkspace]);

  /**
   * Identifies a single permissions request.
   *
   * A scope key alone is not a request identity: with rapid A -> B -> A
   * navigation both requests for A match the current key, so a slow first
   * response can land after a newer one and reinstate permissions the server
   * has since revoked. A monotonically increasing token accepts only the latest
   * invocation, whatever its scope.
   */
  const permissionsRequestReference = useRef(0);

  useEffect(() => {
    if (authStatus !== "authenticated" || workspaceStatus !== "loaded" || hydratedIdentityReference.current !== identityKey) {
      return;
    }

    const scope: PermissionScope = {
      teamId: activeTeamId || undefined,
      projectId: activeProjectId || undefined,
    };

    setActiveScope(scope);

    // An empty workspace has nothing to ask about, and the backend rejects a
    // scopeless request with a 400.
    if (!hasScope(scope)) {
      return;
    }

    const requestId = permissionsRequestReference.current + 1;
    permissionsRequestReference.current = requestId;
    const generation = authGenerationReference.current;
    markLoading(scope);

    void (async () => {
      try {
        const permissions = await getMyPermissionsService.getMyPermissionsAsync(scope);
        if (permissionsRequestReference.current !== requestId || authGenerationReference.current !== generation) return;
        setPermissions(scope, permissions);
      } catch (error) {
        if (permissionsRequestReference.current !== requestId || authGenerationReference.current !== generation) return;
        setPermissionError(scope, error);
      }
    })();
  }, [activeProjectId, activeTeamId, authStatus, getMyPermissionsService, markLoading, permissionsRefetchNonce, setActiveScope, setPermissionError, setPermissions, workspaceStatus]);

  return { reload: loadWorkspace };
};
